import type { CustomerLookupItemDTO } from "@/modules/performance/domain/contracts/customers";
import { Input, RHF, useDebounceCallback } from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

async function searchCustomers(query: string) {
  const url = new URL(`${config.apiUrl}/requests/customers/lookup`);
  if (query.trim()) url.searchParams.set("q", query.trim());
  url.searchParams.set("limit", "8");
  url.searchParams.set("skip", "0");
  return fetcher.get<{ items: CustomerLookupItemDTO[] }>(url.toString());
}

export function CustomerNameAutocomplete() {
  const { control, setValue } = RHF.useFormContext();
  const { Controller } = RHF;
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const debouncedSetSearch = useDebounceCallback((value: string) => {
    setSearch(value);
  }, 300);

  const lookupQuery = useQuery({
    queryKey: ["performance-customers", "lookup", search],
    queryFn: () => searchCustomers(search),
    enabled: open && search.trim().length >= 1,
    staleTime: 30_000,
  });

  const items = lookupQuery.data?.items ?? [];

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const applyCustomer = (item: CustomerLookupItemDTO) => {
    setValue("customerName", item.customerName, { shouldDirty: true });
    setValue("contactName", item.contactName, { shouldDirty: true });
    setValue("contactPhone", item.contactPhone, { shouldDirty: true });
    setValue("contactEmail", item.contactEmail, { shouldDirty: true });
    setValue("contactAddress", item.contactAddress, { shouldDirty: true });
    setOpen(false);
  };

  return (
    <Controller
      control={control}
      name="customerName"
      render={({ field }) => (
        <div ref={containerRef} className="relative">
          <Input
            {...field}
            value={(field.value as string | null) ?? ""}
            placeholder="Company / organization"
            onFocus={() => {
              setOpen(true);
              debouncedSetSearch((field.value as string | null) ?? "");
            }}
            onChange={(event) => {
              field.onChange(event.target.value || null);
              debouncedSetSearch(event.target.value);
              setOpen(true);
            }}
            autoComplete="off"
          />

          {open && search.trim().length >= 1 ? (
            <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover shadow-md">
              {lookupQuery.isLoading ? (
                <p className="px-3 py-2 text-muted-foreground text-sm">
                  ກຳລັງຄົ້ນຫາ...
                </p>
              ) : items.length === 0 ? (
                <p className="px-3 py-2 text-muted-foreground text-sm">
                  ບໍ່ພົບລູກຄ້າ — ພິມໃໝ່ໄດ້
                </p>
              ) : (
                <ul>
                  {items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-muted/60"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => applyCustomer(item)}
                      >
                        <span className="font-medium text-sm">
                          {item.customerName}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {item.contactName}
                          {item.contactPhone ? ` · ${item.contactPhone}` : ""}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      )}
    />
  );
}
